import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { isEqual } from 'lodash-es';
import { usePermissions } from '@customerio/hydra-permissions/react';
import type { Customer, PeopleListParams } from '@/services/db/customer/entity';
import { getPeopleQueryOptions, useUnfilteredPeopleCountOptional } from '@/services/db/customer/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useAnalytics } from '@/services/analytics';
import { queryEncode } from '@/utils/condition-query-encoding';
import { isEmptyString } from '@/utils/string';
import {
  areResultsHidden,
  isEmptyFiltersQuery,
  isValidPaginationPage,
  noFiltersApplied,
  summarizeFilterConditions,
} from './utils/list-params';
import { decodePeopleFiltersParam, queryablePeopleFilters } from './components/people-filter-builder/filters-param';
import { normalizePeopleFilterWire } from './components/people-filter-builder/people-filter-format';
import { asJsonObject, type JsonValue } from './components/people-filter-builder/wire-types';

export const PER_PAGE = 50;

export type PeopleSearchBy = 'email' | 'filter';

const PEOPLE_LIST_PARSERS = {
  email: parseAsString.withDefault(''),
  filters: parseAsString,
  page: parseAsInteger.withDefault(1),
  source: parseAsString,
};

/** Analytics labels for the `source` attribution param carried by inbound links. */
const SOURCE_LABELS = new Map([
  ['segment_overview', 'Segment Overview Tab'],
  ['segment_people', 'Segment People Tab'],
]);

type PeopleListQuery = ReturnType<typeof usePeopleListQuery>;
type SetPeopleParams = ReturnType<typeof useQueryStates<typeof PEOPLE_LIST_PARSERS>>[1];

function usePeopleListQuery(workspaceId: number, { email, filters, page }: PeopleListQueryInputs) {
  const queryParams: PeopleListParams = useMemo(() => {
    const query: PeopleListParams = { page, sort: 'created_at', asc: false };
    // The queried copy is normalized (legacy-array conversion, broken
    // campaign-wildcard repair) so the list, the export, and bulk delete all
    // see the same audience; the URL param itself is never rewritten.
    const queryable = queryablePeopleFilters(filters);
    if (queryable != null && !isEmptyString(queryable)) query.filters = queryable;
    if (!isEmptyString(email)) query.email = email;
    return query;
  }, [page, filters, email]);

  // Always fresh (`staleTime: 0`): the add/merge flows are still Ember-owned,
  // so returning from them must never show a cached page. The list is what the
  // page renders, so a failed load is fatal rather than a permanent loader:
  // `throwOnError` sends it to the route error boundary (the same pattern as
  // `useSendingIdentities`).
  return useQuery({ ...getPeopleQueryOptions(workspaceId, queryParams), staleTime: 0, throwOnError: true });
}

interface PeopleListQueryInputs {
  email: string;
  filters: string | null;
  page: number;
}

/**
 * The total to display, held across a refetch. While a load is in flight the
 * query has no data, so the raw total is unknown; publishing zero there would
 * blank the "N people" heading and render a self-contradictory `51–0 of 0`
 * range. Mirrors the previous page, which assigned its total only when a query
 * resolved and therefore kept the last one on screen throughout.
 *
 * Zero is a real total, so it is retained like any other; only the absence of
 * data falls back. The first load has nothing to retain and reports zero.
 */
function useRetainedTotalCount(total: number | undefined): number {
  const lastKnownTotal = useRef(0);
  if (total != null) lastKnownTotal.current = total;
  return lastKnownTotal.current;
}

type WelcomeDecision = 'welcome' | 'list' | null;

/**
 * `null` while undecided. The probe checks the unfiltered total (not row
 * count) because permissions can hide rows the workspace does have; a failed
 * probe falls back to the regular empty state.
 */
function resolveWelcomeDecision(
  listQuery: PeopleListQuery,
  probe: ReturnType<typeof useUnfilteredPeopleCountOptional>,
): WelcomeDecision {
  if (!listQuery.isSuccess) return null;
  if (listQuery.data.customers.length > 0) return 'list';
  if (probe.isSuccess) return probe.data === 0 ? 'welcome' : 'list';
  if (probe.isError) return 'list';
  return null;
}

/**
 * The welcome-vs-list decision. Latched: once the list renders it never flips
 * back to the welcome state within this visit, so the table (and anything the
 * user is interacting with) stays mounted across page/filter changes.
 */
function useWelcomeDecision(workspaceId: number, listQuery: PeopleListQuery): WelcomeDecision {
  const { track } = useAnalytics();
  const [decision, setDecision] = useState<WelcomeDecision>(null);

  const probe = useUnfilteredPeopleCountOptional({
    workspaceId,
    enabled: decision == null && listQuery.isSuccess && listQuery.data.customers.length === 0,
  });

  const resolved = decision ?? resolveWelcomeDecision(listQuery, probe);
  if (decision == null && resolved != null) {
    setDecision(resolved);
  }

  useEffect(() => {
    if (decision === 'welcome') {
      void track('people', 'emptyStateViewed', {});
    }
  }, [decision, track]);

  return decision;
}

/** Clamp an out-of-range page back to 1 once the result set is known (page 1 never needs rewriting). */
function usePageClamp(listQuery: PeopleListQuery, page: number, setParams: SetPeopleParams): void {
  const pagination = listQuery.data?.meta.pagination;
  useEffect(() => {
    if (pagination == null || page === 1) return;
    if (!isValidPaginationPage({ total: pagination.total, size: pagination.size, page })) {
      void setParams({ page: 1 }, { history: 'replace' });
    }
  }, [pagination, page, setParams]);
}

/**
 * The list-viewed analytics event, fired once per completed load; the
 * `source` attribution param is consumed (tracked once, then dropped from the
 * URL). The stamp ref keeps param changes between loads from re-firing the
 * event for a load already reported.
 */
function useListViewedTracking(
  listQuery: PeopleListQuery,
  { email, filters, source }: { email: string; filters: string | null; source: string | null },
  setParams: SetPeopleParams,
): void {
  const { track } = useAnalytics();
  const { dataUpdatedAt, isFetching } = listQuery;
  const lastTrackedStamp = useRef(0);
  useEffect(() => {
    // While fetching, any data on screen is a cached page whose load was
    // already reported — tracking it would double-count back-navigations.
    if (isFetching || dataUpdatedAt === 0 || lastTrackedStamp.current === dataUpdatedAt) return;
    lastTrackedStamp.current = dataUpdatedAt;
    const summary = summarizeFilterConditions(filters);
    void track('people', 'listViewedSearchedFiltered', {
      'Filter Operator': summary.operator,
      'Filtered by Email Address': !isEmptyString(email),
      'Email Filter Includes Wildcard': email.includes('*'),
      'Number of Segment Conditions': summary.segments,
      'Number of Attribute Conditions': summary.attributes,
      'Number of Newsletter Conditions': summary.newsletters,
      'Number of Message Conditions': summary.messages,
      'Number of Event Conditions': summary.events,
      'Number of Relationship Attribute Conditions': summary.relationship_attributes,
      // undefined serializes away, so an unattributed load carries no Source key.
      Source: source == null ? undefined : SOURCE_LABELS.get(source),
    });
    if (source != null) {
      void setParams({ source: null }, { history: 'replace' });
    }
  }, [isFetching, dataUpdatedAt, filters, email, source, track, setParams]);
}

/** How long a builder edit sits before it commits to the URL and re-queries. */
const FILTER_COMMIT_DEBOUNCE_MS = 400;

/**
 * The live-commit bridge from the condition builder to the `filters` URL
 * param. Every builder change lands here; a commit is scheduled only while
 * the tree is valid or empty (an incomplete row cancels any pending commit
 * and never reaches the URL), and each edit restarts the debounce.
 *
 * The echo check runs at commit time against the then-current param: a
 * notification that merely reflects an external filters change (a deep link
 * landing, a param the page itself just wrote) serializes to the same tree
 * and is dropped, so the URL is never rewritten with re-encoded bytes for a
 * wire the builder didn't edit.
 */
function usePeopleFilterCommit(filters: string | null, setParams: SetPeopleParams, onCommitEmpty: () => void) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filtersRef = useRef(filters);
  const onCommitEmptyRef = useRef(onCommitEmpty);
  useEffect(() => {
    filtersRef.current = filters;
    onCommitEmptyRef.current = onCommitEmpty;
  });

  const cancelPendingFilterCommit = useCallback(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // A pending commit must not outlive the page (mirrors the route-exit
  // cancellation the Ember page performs).
  useEffect(() => cancelPendingFilterCommit, [cancelPendingFilterCommit]);

  const applyBuilderFilters = useCallback(
    (serialized: JsonValue, isValid: boolean, isEmpty: boolean) => {
      cancelPendingFilterCommit();
      if (!isValid && !isEmpty) {
        return;
      }
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        const current = normalizePeopleFilterWire(decodePeopleFiltersParam(filtersRef.current));
        if (isEqual(serialized, current)) {
          return;
        }
        const envelope = asJsonObject(serialized);
        if (envelope == null) {
          return;
        }
        if (isEmpty) {
          // Clearing removes the param; without an active filter forcing
          // conditions mode, the page would snap back to the email choice.
          onCommitEmptyRef.current();
        }
        void setParams({ filters: isEmpty ? null : queryEncode(envelope), page: 1 });
      }, FILTER_COMMIT_DEBOUNCE_MS);
    },
    [cancelPendingFilterCommit, setParams],
  );

  return { applyBuilderFilters, cancelPendingFilterCommit };
}

/**
 * URL state, the list query, and everything derived from them for the People
 * list page: search mode, welcome-vs-list decision, hidden-results state,
 * page clamping, and the list-viewed analytics event.
 */
export function usePeopleListPage() {
  const workspaceId = requireWorkspaceId();
  const { hasPermission } = usePermissions();
  const [params, setParams] = useQueryStates(PEOPLE_LIST_PARSERS, { history: 'push' });
  const { email, filters, page, source } = params;

  const listQuery = usePeopleListQuery(workspaceId, { email, filters, page });
  const people: readonly Customer[] = listQuery.data?.customers ?? [];
  const pagination = listQuery.data?.meta.pagination;
  const totalCount = useRetainedTotalCount(pagination?.total);

  // Search mode: an active condition filter forces "filter"; otherwise the
  // user's explicit choice stands.
  const [searchByChoice, setSearchByChoice] = useState<PeopleSearchBy>('email');
  const activeSearchBy: PeopleSearchBy = !isEmptyFiltersQuery(filters) ? 'filter' : searchByChoice;

  const { applyBuilderFilters, cancelPendingFilterCommit } = usePeopleFilterCommit(filters, setParams, () =>
    setSearchByChoice('filter'),
  );

  const activateSearchBy = (searchBy: PeopleSearchBy) => {
    if (searchBy === activeSearchBy) return;
    // Switching modes discards the other mode's query, including one still
    // inside its commit debounce.
    cancelPendingFilterCommit();
    setSearchByChoice(searchBy);
    void setParams({ email: '', filters: null });
  };

  const search = (term: string) => {
    // An unchanged term still refetches — the Search button always re-runs the query.
    const unchanged = email === term && page === 1;
    void setParams({ email: term, page: 1 });
    if (unchanged) void listQuery.refetch();
  };

  const welcomeDecision = useWelcomeDecision(workspaceId, listQuery);
  usePageClamp(listQuery, page, setParams);
  useListViewedTracking(listQuery, { email, filters, source }, setParams);

  const canEdit = hasPermission('journeys/people/edit');
  const hasHiddenResults =
    listQuery.isSuccess &&
    pagination != null &&
    areResultsHidden({ total: pagination.total, page: pagination.page, rowCount: people.length });

  return {
    workspaceId,
    email,
    filters,
    page,
    setPage: (nextPage: number) => void setParams({ page: nextPage }),
    activeSearchBy,
    activateSearchBy,
    search,
    applyBuilderFilters,
    listQuery,
    people,
    totalCount,
    message: listQuery.data?.meta.message ?? null,
    welcomeDecision,
    canEdit,
    hasHiddenResults,
    hasFilters: filters != null || !isEmptyString(email),
    noFiltersApplied: noFiltersApplied({ filters, email }),
    selectAllDisabled: !canEdit || totalCount < 0 || people.length === 0,
  };
}

/**
 * Row-selection state with the cross-page select-all semantics: checking
 * every row (or clicking Select all) marks the whole result set selected;
 * the raw flag survives per-row math so bulk actions can tell "all matching
 * the filter" from "all on this page". Selection resets whenever a load
 * completes, matching the previous page lifecycle.
 */
export function usePeopleSelection({
  people,
  totalCount,
  dataStamp,
  pendingDeletionIds,
}: {
  people: readonly Customer[];
  totalCount: number;
  /** `dataUpdatedAt` of the list query — a changed stamp clears the selection. */
  dataStamp: number;
  pendingDeletionIds?: ReadonlySet<string>;
}) {
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [allSelectedFlag, setAllSelectedFlag] = useState(false);

  const [prevStamp, setPrevStamp] = useState(dataStamp);
  if (dataStamp !== prevStamp) {
    setPrevStamp(dataStamp);
    setRowSelection({});
    setAllSelectedFlag(false);
  }

  const selectedPeople = useMemo(
    () => people.filter((person) => rowSelection[person.id] === true),
    [people, rowSelection],
  );

  const allSelected = (totalCount > 0 && totalCount === selectedPeople.length) || allSelectedFlag;

  const onRowSelectionChange = (selection: Record<string, boolean>) => {
    setRowSelection(selection);
    const selectedCount = Object.values(selection).filter(Boolean).length;
    setAllSelectedFlag(selectedCount === totalCount && totalCount > 0);
  };

  const onSelectAll = () => {
    const selection: Record<string, boolean> = {};
    for (const person of people) {
      if (pendingDeletionIds?.has(person.id) !== true) selection[person.id] = true;
    }
    setRowSelection(selection);
    setAllSelectedFlag(true);
  };

  const clearSelection = () => {
    setRowSelection({});
    setAllSelectedFlag(false);
  };

  return { rowSelection, onRowSelectionChange, selectedPeople, allSelected, onSelectAll, clearSelection };
}

/**
 * Reports a surfaced API failure as `User: Error Encountered`, the event the
 * Ember error alert fires whenever it shows a failure. `message` is the copy
 * the user actually sees, so the event and the surface never drift; `source`
 * names the flow that failed.
 */
export function useTrackErrorEncountered() {
  const { track } = useAnalytics();

  return useCallback(
    (source: string, message: string) => {
      void track('user', 'errorEncountered', {
        Message: message,
        'Current URL': window.location.href,
        Source: source,
      });
    },
    [track],
  );
}

export type TrackErrorEncountered = ReturnType<typeof useTrackErrorEncountered>;
